// Analyzer Inbox
//
// Shows lab results that landed in `analyzer_inbox` via the on-prem
// bridge service. Each card represents one sample run by the
// hematology analyzer. The lab tech reviews, picks the right patient
// (auto-suggested when possible), and promotes the result into
// haematology_result.
//
// Three card states match the backend's confidence tiers:
//   high → one unambiguous match → single "Confirm" button
//   low  → multiple candidates  → chooser
//   none → no candidates        → manual search
//
// Auto-refreshes every 15 s so newly-arrived results appear without
// the tech having to reload. Refresh pauses while a dialog is open
// to avoid yanking a card out from under the user mid-interaction.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Stack, Alert, CircularProgress, Tooltip,
  Divider, InputAdornment, Collapse, ToggleButtonGroup, ToggleButton,
  Autocomplete,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as DiscardIcon,
  PersonSearch as PickIcon,
  Refresh as RefreshIcon,
  Inbox as InboxIcon,
  Science as ScienceIcon,
  Warning as WarningIcon,
  Help as UnknownIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Done as DoneIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  analyzerInbox, analyzerInboxPromote, analyzerInboxDiscard,
  analyzerInboxReplacePrev,
  analyzerInboxPromoteGroup, analyzerInboxDiscardGroup,
  analyzerInboxPatientRequests,
  loadLabPatients,
} from '../BaseURL';

const REFRESH_MS = 15000;

// Common lab units shown in the unit picker dropdown.
// Ordered by frequency: enzymes first, then proteins, then concentrations.
// freeSolo allows typing anything not in this list.
const COMMON_UNITS = [
  'U/L', 'IU/L',                          // enzymes (AST, ALT, GGT, ALP, LDH)
  'g/L', 'g/dL',                          // proteins (TP, ALB, GLOB)
  'mg/dL', 'mg/L',                        // concentrations (US units)
  'mmol/L',                               // concentrations (SI units)
  'µmol/L', 'nmol/L',                     // bilirubin, creatinine (SI)
  'mEq/L',                               // electrolytes
  'mIU/L', 'mU/L',                        // hormones
  'ratio',                                // pure ratios (A/G, De Ritis)
  '%',                                    // listed last — rarely correct for chemistry
  'fL', 'pg',                             // FBC cell indices
  '10³/µL', '10⁶/µL', '10⁹/L',          // cell counts
];

const CONFIDENCE_STYLES = {
  high: { color: '#2e7d32', bg: 'rgba(46,125,50,0.10)', label: 'Auto-match (high confidence)', icon: <CheckIcon /> },
  low:  { color: '#ed6c02', bg: 'rgba(237,108,2,0.10)', label: 'Multiple candidates — choose one', icon: <WarningIcon /> },
  none: { color: '#9e9e9e', bg: 'rgba(0,0,0,0.06)',    label: 'No match found — pick patient manually', icon: <UnknownIcon /> },
};

// Status-aware card styling. Pending statuses keep the confidence
// colours (driven by the match-suggestion tier above). Terminal
// statuses (matched / discarded) override with a clear "this is done"
// look so the tech can still see the row but isn't tempted to act on it.
const STATUS_STYLES = {
  received:  { kind: 'pending', label: 'Pending review', color: '#11998e', bg: 'rgba(17,153,142,0.10)' },
  parsed:    { kind: 'pending', label: 'Pending review', color: '#11998e', bg: 'rgba(17,153,142,0.10)' },
  matched:   { kind: 'done',    label: 'Imported',       color: '#2e7d32', bg: 'rgba(46,125,50,0.10)' },
  orphan:    { kind: 'pending', label: 'No match',       color: '#ed6c02', bg: 'rgba(237,108,2,0.10)' },
  failed:    { kind: 'pending', label: 'Parse failed',   color: '#d32f2f', bg: 'rgba(211,47,47,0.10)' },
  discarded: { kind: 'done',    label: 'Discarded',      color: '#757575', bg: 'rgba(0,0,0,0.06)' },
};

const sameDay = (d, ref) => {
  const a = new Date(d), b = new Date(ref);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

// FBC sources go through the original "Confirm and save" flow — no per-
// parameter bucket picker, no destination dropdown. The analyzer message
// is unambiguous about its category (every parameter maps to Complete
// Blood Count via analyzer_param_mapping), so the tech only has to
// approve the patient match. Anything not recognised here falls through
// to the BucketAssignment flow, which is the safe default for new /
// unknown analyzers.
const HEMATOLOGY_SOURCE_PATTERN = /medonic|mindray|hematolog|haematolog/i;
const isHematologySource = (source) => HEMATOLOGY_SOURCE_PATTERN.test(String(source || ''));

export default function AnalyzerInbox() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [pickerFor, setPickerFor] = useState(null); // inbox item open in patient picker
  // Manually-confirmed patient overrides: { groupKey → patientId }
  // Set when the tech picks a patient via the picker dialog (overrides auto-match).
  const [pickedPatients, setPickedPatients] = useState({});
  // Inbox id currently being promoted. Used to disable the Confirm /
  // Choose / Promote-to-this-patient buttons while the request is in
  // flight — without this guard a double-click sends two promotes; the
  // first marks the item as matched and the second comes back with
  // "Item already matched" (the user-reported 409).
  const [promotingId, setPromotingId] = useState(null);

  // ── View / filter controls ────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter]   = useState('all'); // today | week | all
  const [statusFilter, setStatusFilter] = useState('pending'); // pending | all
  // Track which cards are expanded (default collapsed). Set of inbox ids.
  const [expandedIds, setExpandedIds] = useState(new Set());
  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const dialogOpenRef = useRef(false);

  // ── Load inbox ────────────────────────────────────────────────────
  const loadInbox = useCallback(async () => {
    try {
      const res = await axios.get(analyzerInbox);
      if (res.data?.success) {
        setItems(res.data.items || []);
        setError('');
      } else {
        setError(res.data?.message || 'Failed to load inbox.');
      }
    } catch (err) {
      setError(err?.message || 'Network error.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInbox();
    const id = setInterval(() => {
      // Don't yank a card while the tech has a dialog open.
      if (!dialogOpenRef.current) loadInbox();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [loadInbox]);

  useEffect(() => {
    dialogOpenRef.current = pickerFor !== null;
  }, [pickerFor]);

  // ── Promote / discard ─────────────────────────────────────────────
  // We no longer optimistically remove the card on promote — the backend
  // returns the row again on the next load with status='matched', and the
  // card renders muted with an "Imported" badge instead of disappearing.
  // This gives the tech a visible audit trail of what's been processed.
  // Aggregated promote — the tech has assigned each parameter to a
  // specific lab_request (or marked it skip). We send the explicit
  // assignment array to /analyzerInbox/promoteGroup and the backend
  // saves with no auto-bucketing.
  const promote = async (item, patientId, assignments) => {
    if (promotingId === item.groupKey) return; // already in flight
    setPromotingId(item.groupKey);
    try {
      const res = await axios.post(analyzerInboxPromoteGroup, {
        inboxIds:   item.inboxIds,
        patientId,
        assignments,
        enteredBy: localStorage.getItem('userName') || 'lab-tech',
      });
      if (res.data?.success) {
        // Build a friendly "saved 4 to LFT, 3 to Lipid Profile, …"
        // line from the per-category breakdown. Falls back to the
        // single number when the backend hasn't been upgraded yet.
        const breakdown = Array.isArray(res.data.categoryBreakdown)
          ? res.data.categoryBreakdown.map(b => `${b.count} to ${b.category}`).join(', ')
          : '';
        const fulfilled = Array.isArray(res.data.fulfilledRequests) && res.data.fulfilledRequests.length > 0
          ? `<br/><small style="color:#2e7d32"><b>Auto-fulfilled:</b> ${res.data.fulfilledRequests.map(r => `${r.category} (req ${r.labNo || r.reqNo})`).join(', ')}</small>`
          : '';
        const skippedNote  = res.data.skipped  ? `<br/><small>${res.data.skipped} skipped</small>` : '';
        const unmappedNote = res.data.unmapped ? `<br/><small style="color:#d32f2f">${res.data.unmapped} unmapped — not saved</small>` : '';
        Swal.fire({
          icon: 'success', title: 'Promoted',
          html: breakdown
            ? `Saved ${breakdown}.${fulfilled}${unmappedNote}${skippedNote}`
            : `${res.data.inserted} parameter(s) written to the patient's results.${fulfilled}${skippedNote}${unmappedNote}`,
          timer: 3200, showConfirmButton: false,
        });
        // Collapse the now-imported card so the next pending one is in view
        setExpandedIds(prev => { const n = new Set(prev); n.delete(item.groupKey); return n; });
        await loadInbox();
        setPickerFor(null);
      } else {
        Swal.fire({ icon: 'error', title: 'Promotion failed', text: res.data?.message || 'Unknown error.' });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.message || err?.message || 'Network error.';
      if (status === 409) {
        await loadInbox();
        Swal.fire({
          icon: 'info', title: 'Already processed',
          text: 'This item was already promoted or discarded — list refreshed.',
          timer: 2000, showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Promotion failed', text: msg });
      }
    } finally {
      setPromotingId(null);
    }
  };

  // The analyzer sometimes sends the same sample twice — the bridge
  // retried, or the tech hit "save" on the analyzer twice, or the
  // sample was deliberately rerun. The newest card is what the tech
  // wants to keep; everything older for the same (sample id, day) is
  // bad data that needs to go. This handler asks for confirmation,
  // then deletes the older sibling cards plus any rows they had
  // already written into the patient's record.
  const replacePrevious = async (item) => {
    const n = (item.olderSiblings || []).length;
    if (n === 0) return;
    const hadPromoted = (item.olderSiblings || []).some(s => s.status === 'matched');
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: `Replace ${n} older result${n === 1 ? '' : 's'}?`,
      html: hadPromoted
        ? `The analyzer sent this sample <b>${n + 1}</b> times today. ` +
          `Some of the older transmissions were already imported into the patient's record. ` +
          `Continuing will <b>permanently delete the older imports</b> and keep only this latest one.<br/><br/>` +
          `<small>Raw bytes remain in the bridge captures/ folder on the clinic PC for audit.</small>`
        : `The analyzer sent this sample <b>${n + 1}</b> times today. ` +
          `Continuing will <b>permanently delete the older duplicate cards</b> and keep only this latest one.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete older, keep this',
      cancelButtonText: 'Cancel',
    });
    if (!isConfirmed) return;
    try {
      const res = await axios.post(analyzerInboxReplacePrev(item.id));
      if (res.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Older results removed',
          html: `Removed ${res.data.removed} duplicate card(s).` +
                (res.data.deletedResults ? `<br/><small>${res.data.deletedResults} result row(s), ${res.data.deletedHistograms} histogram(s), ${res.data.deletedNarratives} note(s) cleaned.</small>` : ''),
          timer: 2200,
          showConfirmButton: false,
        });
        await loadInbox();
      } else {
        Swal.fire({ icon: 'error', title: 'Replace failed', text: res.data?.message || 'Unknown error.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Replace failed', text: err?.response?.data?.message || err?.message || 'Network error.' });
    }
  };

  // Discard the whole group at once — one click clears all N
  // transmissions of the same sample, instead of forcing the tech to
  // discard 9 rows one at a time.
  const discard = async (item) => {
    const n = item.transmissionCount || (item.inboxIds || []).length || 1;
    const { isConfirmed, value } = await Swal.fire({
      icon: 'question',
      title: n > 1 ? `Discard all ${n} transmissions?` : 'Discard this result?',
      input: 'text', inputPlaceholder: 'Reason (optional, e.g. "QC sample")',
      showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Discard',
    });
    if (!isConfirmed) return;
    try {
      await axios.post(analyzerInboxDiscardGroup, {
        inboxIds: item.inboxIds || [],
        reason:   value || '',
      });
      await loadInbox();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Discard failed', text: err?.message || 'Network error.' });
    }
  };

  // ── Derive the visible list ───────────────────────────────────────
  // Filter chain: status -> date -> search. The backend already sorts
  // newest-first; we preserve that order through filtering.
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const now = new Date();
    return items.filter((it) => {
      // Status filter
      if (statusFilter === 'pending') {
        const k = STATUS_STYLES[it.status]?.kind;
        if (k !== 'pending') return false;
      }
      // Date filter
      const ref = it.sampleDate || it.receivedAt;
      if (dateFilter === 'today' && !sameDay(ref, now)) return false;
      if (dateFilter === 'week') {
        const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 7);
        if (new Date(ref) < cutoff) return false;
      }
      // Search filter — patient name, sample id, or matched patient id
      if (q) {
        const haystack = [
          it.patientName, it.sampleId,
          it.suggestions?.[0]?.patientId,
          it.suggestions?.[0]?.surname, it.suggestions?.[0]?.name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, searchQuery, dateFilter, statusFilter]);

  const pendingCount = items.filter(it => STATUS_STYLES[it.status]?.kind === 'pending').length;
  const doneCount    = items.filter(it => STATUS_STYLES[it.status]?.kind === 'done').length;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <InboxIcon sx={{ color: '#11998e', fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#144074' }}>
            Analyzer Inbox
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Lab results auto-received from the on-prem hematology analyzer.
            {pendingCount > 0 && <> · <b style={{ color: '#11998e' }}>{pendingCount} pending</b></>}
            {doneCount > 0 && <> · {doneCount} processed</>}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadInbox}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Search + filter toolbar */}
      <Card sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search by patient name, sample ID, or patient ID…"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <ToggleButtonGroup
            size="small" exclusive value={dateFilter}
            onChange={(_, v) => v && setDateFilter(v)}
          >
            <ToggleButton value="today" sx={{ textTransform: 'none', px: 1.5 }}>Today</ToggleButton>
            <ToggleButton value="week"  sx={{ textTransform: 'none', px: 1.5 }}>7 days</ToggleButton>
            <ToggleButton value="all"   sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small" exclusive value={statusFilter}
            onChange={(_, v) => v && setStatusFilter(v)}
          >
            <ToggleButton value="pending" sx={{ textTransform: 'none', px: 1.5 }}>Pending</ToggleButton>
            <ToggleButton value="all"     sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredItems.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <InboxIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {items.length === 0 ? 'No analyzer results yet.' : 'No items match the current filters.'}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {items.length === 0
                ? 'New samples from the analyzer will appear here automatically.'
                : 'Try expanding the date range or clearing the search.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.25}>
          {filteredItems.map(item => (
            <InboxCard
              key={item.groupKey || item.id}
              item={item}
              expanded={expandedIds.has(item.groupKey || item.id)}
              onToggleExpand={() => toggleExpand(item.groupKey || item.id)}
              busy={promotingId === (item.groupKey || item.id)}
              onPromote={(patientId, assignments) => promote(item, patientId, assignments)}
              onDiscard={() => discard(item)}
              onPick={() => setPickerFor(item)}
              onReplacePrev={() => replacePrevious(item)}
              pickedPatientId={pickedPatients[item.groupKey || item.id] || null}
            />
          ))}
        </Stack>
      )}

      {pickerFor && (
        <PatientPickerDialog
          item={pickerFor}
          onClose={() => setPickerFor(null)}
          onPick={(patientId) => {
            // Store the confirmed patient and close the dialog.
            // BucketAssignment will fetch open requests for this patient
            // and the tech then assigns parameters through the normal flow.
            const key = pickerFor.groupKey || pickerFor.id;
            setPickedPatients(prev => ({ ...prev, [key]: patientId }));
            setPickerFor(null);
          }}
        />
      )}
    </Box>
  );
}

// ── A single inbox card ────────────────────────────────────────────────
function InboxCard({ item, expanded, onToggleExpand, busy, onPromote, onDiscard, onPick, onReplacePrev, pickedPatientId }) {
  const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.received;
  const isDone = statusStyle.kind === 'done';
  // Confidence colour only drives the left-border on pending cards;
  // done cards use the status colour so the eye reads "this is settled".
  const conf = CONFIDENCE_STYLES[item.confidence] || CONFIDENCE_STYLES.none;
  // Duplicate-of-this-sample handling: when the analyzer sent the same
  // sample multiple times today, the newest card shows a "Replace older
  // result(s)" action; the older cards get muted with a "superseded by
  // newer transmission" banner so the tech doesn't promote stale data.
  const hasOlderSiblings    = Array.isArray(item.olderSiblings) && item.olderSiblings.length > 0;
  const isSupersededByNewer = !!item.isSupersededByNewer;
  const stripeColor = isSupersededByNewer ? '#9e9e9e' : (isDone ? statusStyle.color : conf.color);
  const [category, setCategory] = useState('Complete Blood Count with Differential');

  const dateLabel = item.sampleDate
    ? new Date(item.sampleDate).toLocaleString()
    : new Date(item.receivedAt).toLocaleString();

  return (
    <Card sx={{
      borderLeft: `4px solid ${stripeColor}`,
      opacity: isSupersededByNewer ? 0.55 : (isDone ? 0.78 : 1),
    }}>
      <CardContent sx={{ '&:last-child': { pb: expanded ? 2 : 1 } }}>
        {/* Duplicate-transmission banner — shown only on cards that
            have a duplicate situation. The newest card gets an action
            to wipe the older ones; the older card gets a muted warning
            that a newer transmission exists and the tech should act on
            that one instead. */}
        {item.isLatestOfDuplicates && hasOlderSiblings && !isDone && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            action={
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={(e) => { e.stopPropagation(); onReplacePrev && onReplacePrev(); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Replace older result{item.olderSiblings.length === 1 ? '' : 's'}
              </Button>
            }
            sx={{ mb: 1, py: 0.25 }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              The analyzer sent this sample {item.olderSiblings.length + 1} times today —
              this is the latest. Click "Replace older result{item.olderSiblings.length === 1 ? '' : 's'}"
              to delete the {item.olderSiblings.length} older transmission{item.olderSiblings.length === 1 ? '' : 's'}.
            </Typography>
          </Alert>
        )}
        {isSupersededByNewer && (
          <Alert severity="info" icon={<WarningIcon fontSize="small" />} sx={{ mb: 1, py: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              A newer transmission of this sample exists below — act on that one instead.
            </Typography>
          </Alert>
        )}
        {/* Header row — always visible. Clicking the row toggles the
            expansion so the tech can keep the list dense and only
            unfold the rows they need to act on. */}
        <Box
          onClick={onToggleExpand}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': { '& .toggle-icon': { color: 'text.primary' } },
          }}
        >
          <ScienceIcon sx={{ color: stripeColor, fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {item.patientName || '(no patient name in message)'}
              {item.patientSex && (
                <Chip label={item.patientSex} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Sample {item.sampleId || '—'} · {dateLabel} · {item.parameters.length} param{item.parameters.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          {/* Status chip (always shown — pending/imported/discarded) */}
          <Chip
            icon={isDone ? (item.status === 'matched' ? <DoneIcon /> : <BlockIcon />) : conf.icon}
            label={isDone ? statusStyle.label : conf.label}
            size="small"
            sx={{
              bgcolor: isDone ? statusStyle.bg : conf.bg,
              color:   isDone ? statusStyle.color : conf.color,
              fontWeight: 700,
              '& .MuiChip-icon': { color: isDone ? statusStyle.color : conf.color },
            }}
          />
          <IconButton size="small" className="toggle-icon" sx={{ color: 'text.secondary' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Expanded body — actions + parameters + histograms */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 1 }} />

        {/* For imported / discarded items show a simple summary of
            what was done; no action UI. Tech can still review parameters
            and histograms below as a reference. */}
        {isDone && (
          <Box sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {item.status === 'matched'
                ? `Imported to the patient record. Open the patient's results to view on the report.`
                : `This sample was discarded and will not be promoted.`}
            </Typography>
          </Box>
        )}

        {!isDone && item.confidence === 'high' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1 }}>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="caption" color="text.secondary">Matched patient</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {item.suggestions[0].surname} {item.suggestions[0].name}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ID {item.suggestions[0].patientId}
                </Typography>
                {item.suggestions[0].matchedBy && (
                  <Chip
                    label={
                      item.suggestions[0].matchedBy === 'sampleId+date' ? 'Matched by sample ID + date' :
                      item.suggestions[0].matchedBy === 'sampleId'      ? 'Matched by sample ID' :
                                                                          'Matched by name'
                    }
                    size="small"
                    sx={{
                      ml: 1, height: 18, fontSize: '0.62rem', fontWeight: 700,
                      bgcolor: item.suggestions[0].matchedBy.startsWith('sampleId') ? 'rgba(46,125,50,0.12)' : 'rgba(17,153,142,0.10)',
                      color:   item.suggestions[0].matchedBy.startsWith('sampleId') ? '#2e7d32' : '#11998e',
                    }}
                  />
                )}
              </Typography>
            </Box>
            {/* Promote button moved into BucketAssignment below — the
                tech now chooses where each parameter lands first, then
                hits Save there. Keeping just the "Pick different
                patient" so they can correct an auto-match. */}
            <Button size="small" onClick={onPick} disabled={busy} startIcon={<PickIcon />} sx={{ textTransform: 'none' }}>
              Pick different patient
            </Button>
          </Box>
        )}

        {!isDone && item.confidence === 'low' && (
          <Box sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Multiple patients with this name have a lab request in the last 24 h. Use "Pick different patient" below to choose the right one.
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {item.suggestions.map(s => (
                <Box key={s.patientId} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>{s.surname} {s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">ID {s.patientId} · {s.gender}</Typography>
                  </Box>
                </Box>
              ))}
              <Button size="small" onClick={onPick} disabled={busy} startIcon={<PickIcon />} sx={{ textTransform: 'none', alignSelf: 'flex-end' }}>
                Pick patient
              </Button>
            </Stack>
          </Box>
        )}

        {!isDone && item.confidence === 'none' && (
          <Box sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              No lab request found in the last 24 h for a patient named "{item.patientName}".
              Search by patient ID or name to assign manually.
            </Typography>
            <Button startIcon={<SearchIcon />} variant="outlined" onClick={onPick} sx={{ textTransform: 'none' }}>
              Search all patients
            </Button>
          </Box>
        )}

        {/* Analyzer-emitted interpretive narratives — WBC / RBC / PLT
            "Message" alerts ("Anemia", "Leucocytosis", "Abnor. PLT
            Distr.", etc.). Shown as an amber comments block above the
            histograms so the tech sees the analyzer's read on the
            sample before they promote. */}
        {Array.isArray(item.narratives) && item.narratives.length > 0 && (
          <Box sx={{
            mt: 1.5, p: 1.25,
            border: '1px solid #f1d59c', borderLeft: '4px solid #d4a017',
            borderRadius: 1.5, bgcolor: '#fdf8ee',
          }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: '#7a5a10', letterSpacing: 0.6, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
              Analyzer Notes
            </Typography>
            {item.narratives.map((n, i) => (
              <Typography key={i} variant="body2" sx={{ color: '#2c3e50', lineHeight: 1.45 }}>
                <Box component="span" sx={{ fontWeight: 700, color: '#7a5a10', mr: 0.5 }}>
                  {n.name || 'Message'}:
                </Box>
                {n.value}
              </Typography>
            ))}
          </Box>
        )}

        {/* Histograms (WBC / RBC / PLT / LS scattergram, etc.) — only
            show when the analyzer included them. We render the base64
            PNG data URIs straight from parser output, no extra
            processing needed. */}
        {Array.isArray(item.histograms) && item.histograms.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#144074', mb: 0.5, display: 'block' }}>
              Distribution graphs
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {item.histograms.map((h, i) => (
                <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: '#fff', minWidth: 180 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {h.name.replace(/\.\s*(BMP|PNG|JPEG|GIF).*$/i, '').trim()}
                  </Typography>
                  <img
                    src={h.dataUri}
                    alt={h.name}
                    style={{ width: '100%', maxWidth: 220, height: 'auto', display: 'block' }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Open lab_requests this promote will auto-fulfill. The
            backend matches by (patient ID + today's date + category).
            Each matched request will get its progress_status flipped
            to 'done' and its lab_no will be the path_no on every
            haematology_result row for that category — so the report
            stitches together exactly as if the tech had clicked
            "Enter Results" on the panel. */}
        {Array.isArray(item.matchedRequests) && item.matchedRequests.length > 0 && (
          <Box sx={{ mt: 1.5, p: 1.25, border: '1px dashed', borderColor: 'rgba(46,125,50,0.35)', borderRadius: 1, bgcolor: 'rgba(46,125,50,0.06)' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', letterSpacing: 0.4, textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
              Will auto-fulfill these open requests:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {item.matchedRequests.map(r => (
                <Chip
                  key={r.reqNo}
                  label={`${r.category} · req ${r.labNo || r.reqNo}`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: '0.7rem',
                    bgcolor: 'rgba(46,125,50,0.16)', color: '#2e7d32',
                    border: '1px solid rgba(46,125,50,0.4)',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* FBC analyzers go through the simple "Confirm and save" flow —
            every parameter on the message maps to one category (Complete
            Blood Count) via analyzer_param_mapping, so there's nothing
            for the tech to assign. Chemistry analyzers get the full
            BucketAssignment UI because their parameters span multiple
            test categories (LFT, KFT, Lipid, etc.) and the tech has to
            tell us which lab_request each row belongs to. */}
        {!isDone && (
          isHematologySource(item.source) ? (
            <SimpleHematologyPromote
              item={item}
              busy={busy}
              patientId={pickedPatientId || item.suggestions?.[0]?.patientId || null}
              onPromote={onPromote}
              onPick={onPick}
            />
          ) : (
            <BucketAssignment
              item={item}
              busy={busy}
              patientId={pickedPatientId || item.suggestions?.[0]?.patientId || null}
              onPromote={onPromote}
              onPick={onPick}
            />
          )
        )}

        {/* Discard — only for actionable items */}
        {!isDone && (
          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small" startIcon={<DiscardIcon />} color="error"
              onClick={onDiscard} sx={{ textTransform: 'none' }}
            >
              Discard (control / repeat)
            </Button>
          </Box>
        )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ── Patient picker dialog ──────────────────────────────────────────────
// Used for the "pick different patient" or "no match found" flows. Pulls
// the full lab patient list and lets the tech search by patient ID
// (preferred) or name. The search box pre-fills with the patient ID from
// the analyzer message when available — the analyzer almost always
// carries the registration ID in PID-3, so the tech rarely has to type
// anything. ID matches sort to the top so the right row is selectable
// with one click.
function PatientPickerDialog({ item, onClose, onPick }) {
  const [patients, setPatients] = useState([]);
  const [search, setSearch]     = useState(item?.patientIdHint || item?.sampleId || item?.patientName || '');
  const [loading, setLoading]   = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(loadLabPatients);
        if (!cancelled) {
          const list = Array.isArray(res.data?.results) ? res.data.results : (Array.isArray(res.data) ? res.data : []);
          setPatients(list);
        }
      } catch (_) { /* swallow */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Rank matches: exact ID > ID prefix > ID substring > name substring.
  // The tech almost always knows the patient ID at this point because the
  // analyzer printed it on the sample tube — so we make sure the matching
  // ID is the first row in the list.
  const q = search.trim().toLowerCase();
  const filtered = (() => {
    if (!q) return patients.slice(0, 50);
    const exactId   = [];
    const idPrefix  = [];
    const idSubstr  = [];
    const nameMatch = [];
    for (const p of patients) {
      const idLc   = String(p.id || '').toLowerCase();
      const nameLc = String(p.fullName || '').toLowerCase();
      if (idLc === q)              exactId.push(p);
      else if (idLc.startsWith(q)) idPrefix.push(p);
      else if (idLc.includes(q))   idSubstr.push(p);
      else if (nameLc.includes(q)) nameMatch.push(p);
    }
    return [...exactId, ...idPrefix, ...idSubstr, ...nameMatch].slice(0, 50);
  })();

  // Auto-select an unambiguous exact ID match so the tech can just hit
  // "Promote to this patient" without an extra click.
  useEffect(() => {
    if (!q || selectedId) return;
    const exact = patients.find(p => String(p.id || '').toLowerCase() === q);
    if (exact) setSelectedId(exact.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, patients]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select patient for sample {item?.sampleId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            autoFocus fullWidth size="small"
            label="Search by patient ID (preferred) or name"
            placeholder="e.g. JMB_3567"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            helperText={item?.patientIdHint
              ? `Pre-filled with the analyzer's patient ID (${item.patientIdHint}). Edit if it doesn't match a registered patient.`
              : 'Type the registration ID printed on the sample tube — exact matches auto-select.'}
          />
          {/* Categorisation summary — results auto-route to the
              categories the lab admin configured in
              analyzer_param_mapping. No need to pick one here. */}
          {(Array.isArray(item?.categoryBuckets) && item.categoryBuckets.length > 0) || item?.unmappedCount > 0 ? (
            <Box sx={{ p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'rgba(17,153,142,0.04)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#144074', letterSpacing: 0.4, textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                Will be saved under:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(item.categoryBuckets || []).map(b => (
                  <Chip
                    key={b.category}
                    label={`${b.category} · ${b.count}`}
                    size="small"
                    sx={{
                      fontWeight: 700, fontSize: '0.7rem',
                      bgcolor: 'rgba(17,153,142,0.14)', color: '#0c6c63',
                      border: '1px solid rgba(17,153,142,0.3)',
                    }}
                  />
                ))}
                {item.unmappedCount > 0 && (
                  <Tooltip arrow title="These parameters have no mapping. They will be skipped at promote. Ask the lab admin to add them to analyzer_param_mapping.">
                    <Chip
                      label={`${item.unmappedCount} unmapped`}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: '0.7rem',
                        bgcolor: 'rgba(211,47,47,0.12)', color: '#d32f2f',
                        border: '1px solid rgba(211,47,47,0.35)',
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          ) : null}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : filtered.length === 0 ? (
            <Alert severity="info">No patients match your search.</Alert>
          ) : (
            <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {filtered.map(p => {
                const idLc = String(p.id || '').toLowerCase();
                const isExact = q && idLc === q;
                return (
                  <Box
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    sx={{
                      p: 1.25, cursor: 'pointer',
                      bgcolor: selectedId === p.id ? 'rgba(17,153,142,0.14)' : 'transparent',
                      borderBottom: '1px solid', borderColor: 'divider',
                      borderLeft: isExact ? '3px solid #2e7d32' : '3px solid transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small" label={p.id}
                        sx={{
                          height: 20, fontWeight: 700, fontSize: '0.72rem',
                          bgcolor: isExact ? 'rgba(46,125,50,0.16)' : 'rgba(17,153,142,0.10)',
                          color:   isExact ? '#2e7d32' : '#11998e',
                        }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>{p.fullName}</Typography>
                      {isExact && (
                        <Chip size="small" label="exact ID match" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                      {p.gender || '—'}{p.age != null ? ` · ${p.age} yrs` : ''}
                      {p.visitDate ? ` · visit ${p.visitDate}` : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained" disabled={!selectedId}
          onClick={() => onPick(selectedId)}
        >
          Confirm patient
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Simple hematology promote ─────────────────────────────────────────
// FBC analyzers (Medonic / Mindray) emit a single-category sample — every
// OBX parameter belongs to Complete Blood Count — so the tech doesn't
// need to assign each row to a different lab_request. This component
// renders a single "Promote" button that auto-routes every mapped
// parameter to its labCategory and calls promoteGroup in one shot. It
// mirrors the original "Confirm and save" behaviour the inbox had
// before the bucket-assignment redesign.
//
// Unmapped parameters (no analyzer_param_mapping row) are skipped so a
// stray test code doesn't block the save — the unmapped count is shown
// in the summary text so the lab admin can backfill the mapping later.
function SimpleHematologyPromote({ item, busy, patientId, onPromote, onPick }) {
  const parameters = Array.isArray(item.parameters) ? item.parameters : [];

  // Mapped vs unmapped split — the backend skips unmapped rows anyway,
  // but surfacing the count up front avoids confusion when a sample
  // promotes "fewer parameters than it sent".
  const mapped   = parameters.filter(p => (p.labCategory || '').trim());
  const unmapped = parameters.length - mapped.length;

  // Distinct destination categories (usually just one for an FBC sample
  // — included so the user can verify the routing at a glance).
  const destinations = [...new Set(mapped.map(p => p.labCategory))];

  const handlePromote = () => {
    if (!patientId) return;
    const out = mapped.map(p => ({
      parameterName: p.name,
      category:      p.labCategory,
      labTestName:   p.labTestName || p.name,
      reqNo:         null,           // backend auto-fulfills any matching open request
    }));
    onPromote(patientId, out);
  };

  return (
    <Box sx={{
      mt: 1.5, p: 1.25,
      border: '1px dashed', borderColor: 'rgba(46,125,50,0.35)', borderRadius: 1,
      bgcolor: 'rgba(46,125,50,0.06)',
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', letterSpacing: 0.4, textTransform: 'uppercase', display: 'block' }}>
          Ready to save
        </Typography>
        <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.25 }}>
          {mapped.length} parameter{mapped.length === 1 ? '' : 's'} → <b>{destinations.join(', ') || 'Complete Blood Count'}</b>
          {unmapped > 0 && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#d32f2f', fontWeight: 700 }}>
              · {unmapped} unmapped (will be skipped)
            </Typography>
          )}
        </Typography>
      </Box>
      <Button
        size="small" variant="outlined" startIcon={<PickIcon />}
        onClick={onPick} disabled={busy}
        sx={{ textTransform: 'none' }}
      >
        {patientId ? 'Pick different patient' : 'Pick patient'}
      </Button>
      <Button
        size="small" variant="contained" startIcon={<CheckIcon />}
        onClick={handlePromote}
        disabled={!patientId || busy || mapped.length === 0}
        sx={{
          textTransform: 'none', fontWeight: 700,
          bgcolor: '#2e7d32',
          '&:hover': { bgcolor: '#1b5e20' },
        }}
      >
        Promote
      </Button>
    </Box>
  );
}

// ── Bucket assignment ─────────────────────────────────────────────────
// The new core of the inbox card. Replaces the old auto-bucket-and-save
// flow with:
//   • A bucket strip at the top — one chip per open lab_request for the
//     matched patient, with a live count of parameters assigned to it.
//   • A parameter table where each row has a Destination dropdown the
//     tech uses to assign the parameter to a specific request (or skip).
//   • An "Auto-suggest" button that pre-fills the dropdowns using the
//     mapping the backend would have used — but the tech has the final
//     say on every row.
//   • A Save button that's disabled until every parameter has a decision
//     (assigned or skipped), so nothing slips through unattended.
//
// All saves go through analyzerInboxPromoteGroup, which writes only the
// assignments the tech explicitly approved.
function BucketAssignment({ item, busy, patientId, onPromote, onPick }) {
  const parameters = Array.isArray(item.parameters) ? item.parameters : [];

  // Open requests — start from the server-computed list on the card,
  // but re-fetch when the tech manually picks a different patient (the
  // card's matchedRequests were computed for the auto-matched patient).
  const [openRequests, setOpenRequests] = useState(
    Array.isArray(item.matchedRequests) ? item.matchedRequests : []
  );
  const [requestsLoading, setRequestsLoading] = useState(false);

  const prevPatientId = useRef(null); // null = first render, skip initial fetch
  useEffect(() => {
    // Only re-fetch when patientId genuinely changes after the first render.
    // On first render we use item.matchedRequests (already loaded by the server).
    if (prevPatientId.current === null) { prevPatientId.current = patientId; return; }
    if (patientId === prevPatientId.current) return;
    prevPatientId.current = patientId;
    if (!patientId) { setOpenRequests([]); return; }
    setRequestsLoading(true);
    axios.get(analyzerInboxPatientRequests(patientId))
      .then(res => { if (res.data?.success) setOpenRequests(res.data.requests || []); })
      .catch(() => {})
      .finally(() => setRequestsLoading(false));
  }, [patientId]);

  const requestedCats = useMemo(() => new Set(openRequests.map(r => r.category)), [openRequests]);
  const reqByCategory = useMemo(() => {
    const m = new Map();
    for (const r of openRequests) m.set(r.category, r);
    return m;
  }, [openRequests]);

  // Per-parameter assignment state.  value: 'category-name' | '__skip__' | ''
  const [assignments, setAssignments] = useState(() => {
    const init = {};
    for (const p of parameters) {
      const key = String(p.name || '').toLowerCase();
      if (key) init[key] = '';
    }
    return init;
  });

  // Per-parameter unit overrides — only populated when the tech
  // explicitly changes a unit. Empty string is a valid override (= strip unit).
  const [unitOverrides, setUnitOverrides] = useState({});
  const setUnitFor = (paramName, value) =>
    setUnitOverrides(prev => ({ ...prev, [String(paramName || '').toLowerCase()]: value }));

  // A unit is "suspicious" when the analyzer sent "%" for a non-percent
  // test, or when the unit looks like a raw flag value. The tech should
  // confirm or clear it before saving.
  const isSuspiciousUnit = (p) => {
    const u = (p.unit || '').trim();
    if (u === '%') return true;              // almost always wrong for chemistry ratios
    if (u === '-' || u === '—') return true; // placeholder, not a real unit
    return false;
  };

  // Filter toggle — "show only unit issues" so the tech can scan quickly
  const [unitIssueFilter, setUnitIssueFilter] = useState(false);
  const unitIssueCount = parameters.filter(p => {
    const key = String(p.name || '').toLowerCase();
    return isSuspiciousUnit(p) && assignments[key] !== '__skip__' && !(key in unitOverrides);
  }).length;
  // Auto-close the filter once all issues are resolved
  useEffect(() => {
    if (unitIssueCount === 0) setUnitIssueFilter(false);
  }, [unitIssueCount]);

  // Auto-suggest — pre-fill assignments using each parameter's mapped
  // labCategory. Tries exact match first, then substring match so that
  // mapping "Liver Function Test" still routes to "Liver Function Test + LDH".
  // Doesn't overwrite a decision the tech already made manually.
  const autoSuggest = useCallback((requests, forceAll = false) => {
    const reqs = requests || openRequests;
    setAssignments(prev => {
      const next = { ...prev };
      for (const p of parameters) {
        const key = String(p.name || '').toLowerCase();
        if (!key) continue;
        if (!forceAll && next[key]) continue; // don't overwrite
        const cat = (p.labCategory || '').trim();
        if (!cat) continue;
        const catLc = cat.toLowerCase();
        // Exact match first
        const exactMatch = reqs.find(r => r.category === cat);
        if (exactMatch) { next[key] = exactMatch.category; continue; }
        // Fuzzy: request category contains our mapping, or vice versa
        const fuzzyMatch = reqs.find(r => {
          const rLc = (r.category || '').toLowerCase();
          return rLc.includes(catLc) || catLc.includes(rLc);
        });
        if (fuzzyMatch) { next[key] = fuzzyMatch.category; continue; }
      }
      return next;
    });
  }, [parameters, openRequests]);

  // Auto-suggest on first load (once openRequests and patientId are set).
  const didAutoSuggest = useRef(false);
  useEffect(() => {
    if (!patientId || openRequests.length === 0) return;
    if (didAutoSuggest.current) return;
    didAutoSuggest.current = true;
    autoSuggest(openRequests);
  }, [patientId, openRequests, autoSuggest]);

  const setAssignmentFor = (paramName, value) => {
    setAssignments(prev => ({ ...prev, [String(paramName || '').toLowerCase()]: value }));
  };

  // Bulk action — fill every blank with Skip so the tech can save fast
  // when they only care about a subset.
  const skipAllBlanks = () => {
    setAssignments(prev => {
      const next = { ...prev };
      for (const p of parameters) {
        const key = String(p.name || '').toLowerCase();
        if (!key) continue;
        if (!next[key]) next[key] = '__skip__';
      }
      return next;
    });
  };

  // Live counts per bucket + unassigned + skipped.
  const counts = useMemo(() => {
    const c = { unassigned: 0, skipped: 0, byCategory: {} };
    for (const p of parameters) {
      const key = String(p.name || '').toLowerCase();
      const a = assignments[key];
      if (!a) c.unassigned += 1;
      else if (a === '__skip__') c.skipped += 1;
      else c.byCategory[a] = (c.byCategory[a] || 0) + 1;
    }
    return c;
  }, [assignments, parameters]);

  const canSave = patientId
    && parameters.length > 0
    && counts.unassigned === 0
    && (parameters.length - counts.skipped) > 0;

  const handleSave = () => {
    if (!canSave) return;
    const out = [];
    for (const p of parameters) {
      const key = String(p.name || '').toLowerCase();
      const a = assignments[key];
      if (!a || a === '__skip__') {
        out.push({ parameterName: p.name, skip: true });
        continue;
      }
      const req = reqByCategory.get(a);
      const entry = {
        parameterName: p.name,
        category:      a,
        labTestName:   p.labTestName || p.name,
        reqNo:         req ? req.reqNo : null,
      };
      // Unit priority:
      //  1. Tech's explicit pick from the dropdown → use it
      //  2. Suspicious original unit (e.g. "%") → strip it (send '' so
      //     the backend never saves a wrong unit silently)
      //  3. Normal original unit → let backend catalog logic handle it
      if (key in unitOverrides) {
        entry.unitOverride = unitOverrides[key];
      } else if (isSuspiciousUnit(p)) {
        entry.unitOverride = '';  // strip "%" etc. silently
      }
      out.push(entry);
    }
    onPromote(patientId, out);
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      {/* Bucket strip — one chip per open request for the patient. */}
      {openRequests.length > 0 ? (
        <Box sx={{ mb: 1.5, p: 1.25, border: '1px dashed', borderColor: 'rgba(46,125,50,0.35)', borderRadius: 1, bgcolor: 'rgba(46,125,50,0.06)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', letterSpacing: 0.4, textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
            Open requests for this patient · click a parameter's destination to assign
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {openRequests.map(r => {
              const n = counts.byCategory[r.category] || 0;
              return (
                <Chip
                  key={r.reqNo}
                  label={`${r.category} · ${n} param${n === 1 ? '' : 's'}`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: '0.72rem',
                    bgcolor: n > 0 ? 'rgba(46,125,50,0.22)' : 'rgba(46,125,50,0.10)',
                    color:   n > 0 ? '#1b5e20' : '#2e7d32',
                    border:  n > 0 ? '1px solid rgba(46,125,50,0.55)' : '1px solid rgba(46,125,50,0.3)',
                  }}
                />
              );
            })}
            <Chip
              label={`Skip · ${counts.skipped}`}
              size="small"
              sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary' }}
            />
            {counts.unassigned > 0 && (
              <Chip
                label={`Unassigned · ${counts.unassigned}`}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: '0.72rem',
                  bgcolor: 'rgba(211,47,47,0.12)', color: '#d32f2f',
                  border: '1px solid rgba(211,47,47,0.35)',
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="small" onClick={() => autoSuggest(null, false)} disabled={busy} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
              Re-suggest from mappings
            </Button>
            <Button size="small" onClick={skipAllBlanks} disabled={busy} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
              Skip all unassigned
            </Button>
            {unitIssueCount > 0 && (
              <Chip
                label={`${unitIssueCount} unit issue${unitIssueCount === 1 ? '' : 's'} — click to review`}
                size="small"
                onClick={() => setUnitIssueFilter(v => !v)}
                sx={{
                  fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                  bgcolor: unitIssueFilter ? 'rgba(237,108,2,0.22)' : 'rgba(237,108,2,0.10)',
                  color: '#b45309',
                  border: '1px solid rgba(237,108,2,0.4)',
                }}
              />
            )}
            {unitIssueFilter && (
              <Typography variant="caption" sx={{ color: '#b45309', fontStyle: 'italic' }}>
                Showing unit issues only — edit the unit or clear it, then toggle off
              </Typography>
            )}
          </Box>
        </Box>
      ) : requestsLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">Loading open requests…</Typography>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {patientId
              ? 'No open lab requests on file for this patient in the last 3 days. Use "Skip" for all parameters or pick a different patient.'
              : 'Pick a patient first — then you\'ll see their open requests as bucket chips here.'}
          </Typography>
        </Alert>
      )}

      {/* Parameter table with per-row Destination dropdown. */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Table size="small" sx={{ '& th, & td': { py: 0.5, px: 1, fontSize: '0.78rem' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ref. range</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Flag</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Assign to</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.map((p, i) => {
              const key = String(p.name || '').toLowerCase();
              const value = assignments[key] || '';
              const isUnassigned = !value;
              const suspicious = isSuspiciousUnit(p);
              const unitEdited = key in unitOverrides;
              // Suspicious units (like "%") start BLANK so the tech
              // must actively pick the correct unit — we never silently
              // save a wrong unit. Non-suspicious units show as-is.
              const displayUnit = unitEdited
                ? unitOverrides[key]
                : (suspicious ? '' : (p.unit || ''));

              // Unit-issue filter — hide rows that have no unit problem when filter is on
              if (unitIssueFilter && !(suspicious && !unitEdited && value !== '__skip__')) return null;

              // Show only the open request(s) that this parameter belongs
              // to — determined by the mapping's labCategory. Falls back
              // to all open requests when the parameter has no mapping
              // (unmapped → let tech pick from everything).
              const catLc = (p.labCategory || '').toLowerCase();
              const relevantRequests = catLc
                ? (() => {
                    const exact = openRequests.filter(r => r.category === p.labCategory);
                    if (exact.length > 0) return exact;
                    const fuzzy = openRequests.filter(r => {
                      const rLc = (r.category || '').toLowerCase();
                      return rLc.includes(catLc) || catLc.includes(rLc);
                    });
                    return fuzzy.length > 0 ? fuzzy : openRequests;
                  })()
                : openRequests;

              return (
                <TableRow key={i} sx={{ bgcolor: isUnassigned ? 'rgba(211,47,47,0.04)' : 'transparent' }}>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{p.name}</Typography>
                      {p.labTestName && p.labTestName !== p.name && (
                        <Typography sx={{ fontSize: '0.67rem', color: 'text.secondary' }}>
                          → {p.labTestName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>{p.value}</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {suspicious && !unitEdited && value !== '__skip__' && (
                        <Tooltip arrow title={`Analyzer sent "${p.unit}" — pick the correct unit from the list or leave blank for no unit.`}>
                          <WarningIcon sx={{ fontSize: 14, color: '#b45309', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={COMMON_UNITS}
                        value={displayUnit}
                        onChange={(_, v) => setUnitFor(p.name, v ?? '')}
                        onInputChange={(_, v) => setUnitFor(p.name, v)}
                        disabled={busy || value === '__skip__'}
                        disableClearable={false}
                        sx={{ width: 105 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={suspicious && !unitEdited ? 'pick unit' : 'unit'}
                            sx={{
                              '& .MuiInputBase-input': { py: 0.3, px: 0.5, fontSize: '0.75rem' },
                              '& .MuiOutlinedInput-root': {
                                bgcolor: suspicious && !unitEdited && value !== '__skip__'
                                  ? 'rgba(237,108,2,0.08)' : 'transparent',
                                pr: '28px !important',
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>{p.referenceRange || '—'}</TableCell>
                  <TableCell>
                    {p.flag && (
                      <Chip
                        label={p.flag} size="small"
                        sx={{
                          height: 18, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: p.flag === 'normal' ? 'rgba(46,125,50,0.1)' : p.flag === 'low' ? 'rgba(237,108,2,0.1)' : p.flag === 'high' ? 'rgba(211,47,47,0.1)' : 'rgba(0,0,0,0.06)',
                          color:   p.flag === 'normal' ? '#2e7d32' : p.flag === 'low' ? '#ed6c02' : p.flag === 'high' ? '#d32f2f' : 'text.primary',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" select
                      value={value}
                      onChange={(e) => setAssignmentFor(p.name, e.target.value)}
                      disabled={busy}
                      sx={{ minWidth: 200, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.78rem' } }}
                    >
                      <MenuItem value=""><em>— pick destination —</em></MenuItem>
                      {relevantRequests.map(r => (
                        <MenuItem key={r.reqNo} value={r.category}>
                          {r.category}
                          {relevantRequests.length === openRequests.length && p.labCategory && (
                            <Typography component="span" sx={{ ml: 0.5, fontSize: '0.67rem', color: 'text.secondary' }}>
                              (no exact match)
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                      <MenuItem value="__skip__"><em>Skip (don't save)</em></MenuItem>
                    </TextField>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Save bar */}
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {counts.unassigned > 0
            ? `${counts.unassigned} parameter${counts.unassigned === 1 ? '' : 's'} still unassigned`
            : counts.skipped === parameters.length
              ? 'Every parameter is set to Skip — nothing to save.'
              : `Ready: ${parameters.length - counts.skipped} parameter${(parameters.length - counts.skipped) === 1 ? '' : 's'} will be saved` +
                (counts.skipped > 0 ? `, ${counts.skipped} skipped.` : '.')}
        </Typography>
        <Button
          variant="contained"
          disabled={!canSave || busy}
          startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <CheckIcon />}
          sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none' }}
          onClick={handleSave}
        >
          {busy ? 'Saving…' : 'Save assignments'}
        </Button>
      </Box>
    </Box>
  );
}
